import React, { useMemo } from 'react';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	items: {
		display: 'grid',
		gap: token('space.100'),
	},
	card: {
		borderRadius: token('radius.large'),
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		backgroundColor: token('elevation.surface.sunken'),
	},
	fill: {
		flex: '1',
		minWidth: '0px',
	},
});

export type LoadingSkeletonProps = {
	/**
	 * Grid column count. Use `2` for the list’s grid (`1fr 1fr`), `1` for a vertical stack.
	 */
	columnCount: 1 | 2;
	/** Number of placeholder cards (e.g. 8 for 2×4, 4 or 3 for stacks). */
	itemCount: number;
	testId?: string;
};

function SkeletonCard() {
	return (
		<Box xcss={styles.card}>
			<Inline space="space.150" alignBlock="center">
				<Skeleton width="32px" height="32px" borderRadius={token('radius.large')} />
				<Stack space="space.100" xcss={styles.fill}>
					<Skeleton width="45%" height="12px" borderRadius={token('radius.small')} />
					<Skeleton width="72%" height="12px" borderRadius={token('radius.small')} />
				</Stack>
			</Inline>
		</Box>
	);
}

export function LoadingSkeleton({
	columnCount,
	itemCount,
	testId = 'user-inline-preview-loading-skeleton',
}: LoadingSkeletonProps): React.JSX.Element {
	const placeholders = useMemo(
		() => Array.from({ length: Math.max(0, itemCount) }, (_, index) => index),
		[itemCount],
	);

	return (
		<Stack space="space.075" testId={testId}>
			<Box
				xcss={styles.items}
				style={{ gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))` }}
			>
				{placeholders.map((key) => (
					<SkeletonCard key={key} />
				))}
			</Box>
		</Stack>
	);
}
